import React from "react";
import { MapPin, Phone, Mail, Instagram, Linkedin, MessageCircle, ArrowUpRight } from "lucide-react";
import { SITE, waLink } from "../lib/site";

export default function Contact() {
  return (
    <section
      id="contato"
      data-testid="contact-section"
      className="relative bg-[#0B101A] py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left — info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <div className="gold-line" />
              <span className="overline">Contato & Matriz</span>
            </div>
            <h2 className="font-serif-ag text-white text-4xl md:text-5xl lg:text-[3.2rem] font-light leading-[1.05] tracking-[-0.015em]">
              Vamos estruturar a sua{" "}
              <span className="text-[#D4AF37] italic">próxima operação</span>.
            </h2>

            <div className="mt-10 space-y-6">
              <div className="flex items-start gap-5 border-b border-white/5 pb-5" data-testid="contact-address">
                <MapPin size={20} strokeWidth={1.3} className="text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#A3ABB8] mb-2">
                    Matriz — São Paulo
                  </div>
                  <div className="text-white text-sm md:text-base font-light leading-relaxed">
                    {SITE.address.street}<br />
                    {SITE.address.building}<br />
                    {SITE.address.city} — {SITE.address.cep}
                  </div>
                </div>
              </div>

              <a
                href={waLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-start gap-5 border-b border-white/5 pb-5 group"
              >
                <Phone size={20} strokeWidth={1.3} className="text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#A3ABB8] mb-2">WhatsApp</div>
                  <div className="text-white text-sm md:text-base font-light group-hover:text-[#D4AF37] transition-colors">
                    {SITE.whatsappDisplay}
                  </div>
                </div>
              </a>

              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email-link"
                className="flex items-start gap-5 border-b border-white/5 pb-5 group"
              >
                <Mail size={20} strokeWidth={1.3} className="text-[#D4AF37] mt-1 shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#A3ABB8] mb-2">E-mail</div>
                  <div className="text-white text-sm md:text-base font-light group-hover:text-[#D4AF37] transition-colors">
                    {SITE.email}
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4 pt-2">
                <a
                  href={SITE.social.instagram}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Instagram"
                  data-testid="social-instagram"
                  className="w-11 h-11 border border-white/10 flex items-center justify-center text-[#A3ABB8] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                >
                  <Instagram size={17} strokeWidth={1.3} />
                </a>
                <a
                  href={SITE.social.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="LinkedIn"
                  data-testid="social-linkedin"
                  className="w-11 h-11 border border-white/10 flex items-center justify-center text-[#A3ABB8] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                >
                  <Linkedin size={17} strokeWidth={1.3} />
                </a>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="WhatsApp"
                  data-testid="social-whatsapp"
                  className="w-11 h-11 border border-white/10 flex items-center justify-center text-[#A3ABB8] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                >
                  <MessageCircle size={17} strokeWidth={1.3} />
                </a>
              </div>
            </div>

            <a
              href={waLink("Olá, gostaria de solicitar uma análise para uma carta contemplada.")}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-cta-main"
              className="mt-10 inline-flex items-center gap-3 bg-[#D4AF37] text-[#05070D] px-7 py-4 text-xs tracking-[0.22em] uppercase font-medium hover:bg-[#F0CC59] transition-colors"
            >
              <MessageCircle size={16} strokeWidth={1.6} />
              Solicitar Análise
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </a>
          </div>

          {/* Right — Map */}
          <div className="lg:col-span-7">
            <div className="relative border border-[#D4AF37]/20 overflow-hidden h-[520px] lg:h-full min-h-[520px]">
              <iframe
                title="ASHIR GROUP — Matriz São Paulo"
                data-testid="contact-map-iframe"
                src="https://www.google.com/maps?q=Rua+Dr.+Guilherme+Bannitz+126+Itaim+Bibi+S%C3%A3o+Paulo&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.6) brightness(0.85)" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="absolute top-4 left-4 bg-[#05070D]/85 backdrop-blur px-4 py-3 border border-[#D4AF37]/30">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">
                  ASHIR GROUP
                </div>
                <div className="text-white text-xs mt-1 font-light">
                  Itaim Bibi · São Paulo
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
